import { InvalidTokenError, jwtDecode } from 'jwt-decode';
import { GenericCollection, IdentityJwtClaim, AccessTokenPayload, RefreshTokenPayload } from '../types/internal-types';
import { LogHelper } from './log.helper';
import { ErrorHelper } from './error.helper';

export class TokenHelper {
	static getPayload<T extends AccessTokenPayload | RefreshTokenPayload>(
		token: string,
		verifyClaims: GenericCollection = {},
		threshold: number = 0
	): T {
		const payload: T = jwtDecode(token);

		/**
		 * throw error if the token is expired
		 */
		if (this.isExpired(payload, 'exp', threshold)) {
			throw new InvalidTokenError('Token expired');
		}

		/**
		 * loop through claims to verify and make sure the payload matches their values
		 */
		Object.keys(verifyClaims).forEach(claim => {
			const tokenClaimValue: unknown = payload[claim];
			const requestedClaimValue: unknown = verifyClaims[claim];

			if (!this.isClaimValid(tokenClaimValue, requestedClaimValue)) {
				throw new InvalidTokenError(
					`Invalid "${claim}" claim value "${tokenClaimValue}" provided in token. Required value is "${requestedClaimValue}"`
				);
			}
		});

		return payload;
	}

	private static isExpired<T extends AccessTokenPayload | RefreshTokenPayload>(
		token: T,
		claim: IdentityJwtClaim = 'exp',
		threshold: number = 0
	): boolean {
		const throwInvalidColumnError: (column: string) => true = (column: string): true => {
			throw new InvalidTokenError(`Column ${column} does not contain a valid number`);
		};

		try {
			/**
			 * get value from the column
			 */
			let timestamp: unknown = token[claim];

			/**
			 * if values is a number - compare it against the current date
			 */
			if (typeof timestamp === 'number') {
				/**
				 * if a threshold is set - extract it from the timestamp
				 * NOTE: a threshold allows considering the timestamp expired earlier;
				 * this allows making sure the token does not expire while being transferred to backend
				 */
				if (threshold > 0) {
					timestamp -= threshold;
				}

				return !!timestamp && timestamp * 1000 < new Date().getTime();
			}

			return throwInvalidColumnError(claim.toString());
		} catch (error) {
			LogHelper.error(new InvalidTokenError(ErrorHelper.extractMessage(error)));

			return true;
		}
	}

	private static isClaimValid(tokenClaim: unknown, valueToVerify: unknown): boolean {
		/**
		 * if any of the values is an array - apply special logic;
		 * otherwise do strict comparison
		 */
		if (tokenClaim instanceof Array || valueToVerify instanceof Array) {
			/**
			 * if both claims are arrays - make sure they intersect
			 */
			if (tokenClaim instanceof Array && valueToVerify instanceof Array) {
				return tokenClaim.filter(value => valueToVerify.includes(value)).length > 0;
			}

			/**
			 * if the first claim is an array and the second is a string -
			 * check if the second is within the first
			 */
			if (tokenClaim instanceof Array && typeof valueToVerify === 'string') {
				return tokenClaim.includes(valueToVerify);
			}

			/**
			 * if the second claim is an array and the first is a string -
			 * check if the first is within the second
			 */
			if (valueToVerify instanceof Array && typeof tokenClaim === 'string') {
				return valueToVerify.includes(tokenClaim);
			}

			return false;
		}

		return tokenClaim === valueToVerify;
	}
}
